/**
 * Typed route helpers — single source of truth for in-app paths.
 *
 * Per coding-standards §12: never hardcode URLs in JSX/code; centralize.
 */
export const APP_ROUTES = {
  /** Orders listing — the migrated React `<Orders />` page (Sales orders > All orders). */
  orders: "/orders",
  /** Sales orders > All orders — the Vue-style URL the listing is reached at
   *  (`/orders` is a direct-link alias). Registered ABOVE the distributor
   *  wildcard so React Router prefers the exact match. */
  ordersSalesOrdersAll: "/orders/sales-orders/all",
  /** Base of the distributor-scoped listings (`<base>/*` is the wildcard;
   *  the registry in `features/orders/utils/distributorContext.ts` decides
   *  which sub-paths render the listing). */
  ordersSalesOrdersBase: "/orders/sales-orders",
  /** Staged (dealer) orders — All, the Jet-Fresh and CW dealer scopes, and
   *  the base the `<base>/*` catch-all is built from. */
  stagedOrdersAll: "/orders/staged-orders/all",
  stagedOrdersJetfresh: "/orders/staged-orders/jetfresh",
  stagedOrdersCwOrder: "/orders/staged-orders/cw-order",
  stagedOrdersBase: "/orders/staged-orders",
  ceoPricing: "/ceo-pricing",
  ceoPricingDetail: "/ceo-pricing/:productId",
  /**
   * Alternative entry paths for the CEO Pricing detail page, accepting the
   * same product id OR SKU param as `ceoPricingDetail`.
   *
   * People arrive with the path they already have in hand — the ERP groups
   * this screen under `/products/*`, and the backing endpoint is
   * `/api/v1/products/pricing/<id>` — so `/products/<sku>` and
   * `/products/pricing/<sku>` both get typed. Both are accepted in the
   * singular too: dropping the `s` is the single most common slip.
   *
   * Every DETAIL alias resolves and then REPLACES the URL with the canonical
   * `/ceo-pricing/<productId>`, so only one path is ever bookmarked or
   * re-shared. The LISTING aliases (`/products/ceo-pricing`, …) render the
   * listing in place and are not rewritten. See `features/ceoPricing/lib/skuDeepLink.ts`.
   *
   * Static sibling routes still win: React Router ranks a literal segment
   * above a dynamic one, so any existing `/product(s)/*` page is unaffected.
   */
  ceoPricingDetailAliases: [
    "/products/:productId",
    "/products/pricing/:productId",
    "/product/:productId",
    "/product/pricing/:productId",
  ],
  /**
   * The listing under those same prefixes, WITHOUT a param.
   *
   * `/products/ceo-pricing` is the ERP's own path for this screen (it is the
   * key in `ROUTE_PERMISSIONS` and the `tbl_routes` row), so it has to land on
   * the listing. Without these it would be read as a SKU and answer "No
   * product found for SKU “ceo-pricing”".
   */
  ceoPricingListingAliases: [
    "/products/ceo-pricing",
    "/product/ceo-pricing",
    "/products/pricing",
    "/product/pricing",
  ],
  root: "/",
  login: "/login",
  dashboard: "/dashboard",
  /** Global-search results (Vue parity: `/search?search=&active=`). */
  search: "/search",
  salesSummary: "/dashboard/sales-summary",
  salesBreakdown: "/dashboard/sales-breakdown",
  arContacts: "/ar-contacts",
  /** Marketing — Ads & Sales Reconciliation dashboard (React-only). */
  amazonAds: "/marketing/amazon-ads",
  authDirect: "/auth/direct",
  shippingPrices: "/orders/shipping-prices",
  spiInventory: "/inventory/spi",
  spiBuyers: "/inventory/spi/buyers",
  spiSettings: "/inventory/spi/settings",
  spi360view: "/inventory/spi/360view",
  closeHub: "/accounting/closehub",
  closeHubDashboard: "/accounting/closehub/dashboard",
  closeHubChecklist: "/accounting/closehub/checklist",
  closeHubRecon: "/accounting/closehub/recon",
  asanaDashboard: "/sourcing/asana",
  asanaSourcing: "/sourcing/asana/sourcing",
  asanaPurchasing: "/sourcing/asana/purchasing",
  apiKeys: "/settings/api-keys",
  apiKeysList: "/settings/api-keys/list",
  apiKeysNew: "/settings/api-keys/NEW_API_KEY",
} as const;

/**
 * Route for one sales order's detail page, by its `refId` (Vue parity:
 * `/orders/sales-orders/:orderId`). There is deliberately no `:orderId`
 * entry in `APP_ROUTES`: the detail page is served from inside the
 * `<base>/*` splat by the segment's shape (`orderRefIdFromSalesOrdersPath`),
 * because a dynamic route would outrank the splat and capture every
 * distributor slug.
 */
export const orderDetailRoute = (refId: string): string =>
  `${APP_ROUTES.ordersSalesOrdersBase}/${encodeURIComponent(refId)}`;

/**
 * Route for one staged (dealer) order's detail page, by its `refId` (Vue
 * parity: `/orders/staged-orders/:orderId` → `StagedOrderDetails.vue`).
 * Served the same way, by `stagedOrderRefIdFromPath` inside the staged splat.
 */
export const stagedOrderDetailRoute = (refId: string): string =>
  `${APP_ROUTES.stagedOrdersBase}/${encodeURIComponent(refId)}`;

/** Route for a specific API key detail / edit screen. */
export const apiKeyDetailRoute = (id: string): string => `/settings/api-keys/${id}`;

/**
 * Static handoff pages served from `public/auth-sync/`. These bypass React
 * Router and run inline `<script>`, used by Vue's iframe sync.
 */
export const STATIC_AUTH_SYNC = {
  handoff: "/auth-sync/handoff.html",
  syncLogin: "/auth-sync/sync-login.html",
  syncLogout: "/auth-sync/sync-logout.html",
  checkSession: "/auth-sync/check-session.html",
  /**
   * Vue-origin page that WRITES Vue's emulation localStorage from URL params,
   * so an emulation started in React mirrors into the Vue ERP. Lives in the
   * Vue repo (`RW-ERP-Service/public/auth-sync/`), loaded cross-origin via
   * `syncStartEmulationToVueApp()`.
   */
  startEmulation: "/auth-sync/start-emulation.html",
  /**
   * Vue-origin page that CLEARS Vue's emulation localStorage, so ending an
   * emulation in React clears it on Vue too. Mirrors `sync-logout.html`. Lives
   * in the Vue repo, loaded via `syncEndEmulationToVueApp()`.
   */
  endEmulation: "/auth-sync/end-emulation.html",
} as const;

/** Path to send the user to when their React session is invalidated. */
export const externalVueLoginUrl = (vueAppUrl: string): string =>
  `${vueAppUrl}${APP_ROUTES.login}`;

/**
 * Vue global-search URL for one order number (Vue parity:
 * `/search?search=<order>&active=orders`).
 *
 * Takes the origin as an argument rather than hardcoding one: the origin must
 * come from `AUTH_CONFIG.VUE_APP_URL` (`REACT_APP_VUE_URL`), so a dev or QA
 * build opens the matching Vue environment. A literal production origin here
 * sent every environment's order links to live data.
 */
export const externalVueOrderSearchUrl = (
  vueAppUrl: string,
  orderNumber: string,
): string =>
  `${vueAppUrl}${APP_ROUTES.search}` +
  `?search=${encodeURIComponent(orderNumber)}&active=orders`;

/**
 * Query-param name used to carry the intended post-login destination through
 * the login detour. Shared by `PrivateRoute` (writes it), `Login` (reads it),
 * `DirectAuth` (reads it), and the static `handoff.html` (which mirrors the
 * literal `'redirect'` — keep them in sync).
 */
export const REDIRECT_QUERY_PARAM = "redirect";

/**
 * Default landing page after a successful sign-in, matching the Vue ERP: its
 * `ProfileLoader.vue` pushes `/orders/sales-orders/all` once the boot
 * dependencies resolve, so a user who logs in lands on the orders listing.
 *
 * This path is a VUE route — deliberately. That module has not been migrated
 * here (there is no `Orders` page, no `/orders` entry in `APP_ROUTES`, and the
 * menu's own "All orders" item carries `isReactRoute: false`), so the orders
 * screen only exists on the Vue origin. Landing there therefore has to be a
 * cross-origin redirect via `externalVueOrdersUrl()`, not a react-router
 * navigate. If the module is ever migrated, add the React route and switch these
 * call sites back to `navigate()`.
 *
 * Vue additionally gates this on `hasPermissionForRoute('/orders/sales-orders/all')`
 * and falls back to `/product/ideation`. That check is NOT repeated here: at the
 * moment login resolves, React has not yet fetched the permission snapshot
 * (`PermissionProvider` mounts after auth), so there is nothing to test against.
 * Vue owns the destination once we arrive, and applies its own route guard.
 */
export const VUE_POST_LOGIN_PATH = "/orders/sales-orders/all";

/** Absolute URL of the Vue orders listing used as the post-login landing page. */
export const externalVueOrdersUrl = (vueAppUrl: string): string =>
  `${vueAppUrl}${VUE_POST_LOGIN_PATH}`;

/**
 * Sanitize a post-login redirect target. Returns the value only when it is a
 * safe SAME-ORIGIN in-app path; otherwise returns `null` to mean "no in-app
 * target was requested" — the caller then sends the user to the default landing
 * page (`externalVueOrdersUrl`). Rejects absolute URLs and protocol-relative
 * (`//host`) values to prevent open-redirect abuse, and never redirects back to
 * the login route (which would loop).
 *
 * Returning `null` rather than a fallback path is what lets the two cases stay
 * distinct: an explicit `?redirect=/ar-contacts` must still win and stay IN this
 * app, while its absence means "wherever login normally lands", which is now
 * cross-origin and cannot be expressed as an in-app path.
 */
export const safeInAppRedirect = (
  value: string | null | undefined
): string | null => {
  if (!value) return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  if (value === APP_ROUTES.login) return null;
  return value;
};

/** Build the in-app login path, preserving the intended destination. */
export const loginPathWithRedirect = (target: string): string => {
  if (!target || target === APP_ROUTES.login) return APP_ROUTES.login;
  return `${APP_ROUTES.login}?${REDIRECT_QUERY_PARAM}=${encodeURIComponent(target)}`;
};
